import { useEffect, useCallback } from 'react';

export interface KeyboardShortcut {
    key: string;
    ctrl?: boolean;
    alt?: boolean;
    shift?: boolean;
    meta?: boolean;
    description: string;
    action: () => void;
    preventDefault?: boolean;
    path?: string;
}

interface UseKeyboardShortcutsOptions {
    enabled?: boolean;
}

/**
 * Custom hook to register keyboard shortcuts
 * @param shortcuts Array of keyboard shortcut configurations
 * @param options Configuration options
 */
export const useKeyboardShortcuts = (shortcuts: KeyboardShortcut[], options: UseKeyboardShortcutsOptions = {}) => {
    const { enabled = true } = options;

    const handleKeyDown = useCallback(
        (event: KeyboardEvent) => {
            if (!enabled) return;

            // Don't trigger shortcuts when typing in input fields or when text is selected
            const target = event.target as HTMLElement;
            const selection = window.getSelection();
            const hasSelection = selection && selection.toString().length > 0;

            if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable || (hasSelection && (event.ctrlKey || event.metaKey))) {
                return;
            }

            shortcuts.forEach((shortcut) => {
                const keyMatches = event.key.toLowerCase() === shortcut.key.toLowerCase();
                const ctrlMatches = shortcut.ctrl ? event.ctrlKey || event.metaKey : !event.ctrlKey && !event.metaKey;
                const altMatches = shortcut.alt ? event.altKey : !event.altKey;
                const shiftMatches = shortcut.shift ? event.shiftKey : !event.shiftKey;
                const metaMatches = shortcut.meta ? event.metaKey : !event.metaKey;

                if (keyMatches && ctrlMatches && altMatches && shiftMatches && metaMatches) {
                    if (shortcut.preventDefault !== false) {
                        event.preventDefault();
                    }
                    shortcut.action();
                }
            });
        },
        [shortcuts, enabled]
    );

    useEffect(() => {
        if (!enabled) return;

        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleKeyDown, enabled]);

    return shortcuts;
};

/**
 * Format keyboard shortcut for display
 * @param shortcut Keyboard shortcut configuration
 * @returns Formatted string for display (e.g., "Ctrl+N")
 */
export const formatShortcut = (shortcut: KeyboardShortcut): string => {
    const parts: string[] = [];

    if (shortcut.ctrl) parts.push('Ctrl');
    if (shortcut.alt) parts.push('Alt');
    if (shortcut.shift) parts.push('Shift');
    if (shortcut.meta) parts.push('⌘');

    parts.push(shortcut.key.toUpperCase());

    return parts.join('+');
};
